import { useEffect, useMemo, useState } from 'react';
import type { ParamDef } from '../api';
import { Field } from './Common';

const DATE_PRESETS: { value: string; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
  { value: 'custom', label: 'Custom range…' },
];

export function defaultParamValues(defs: ParamDef[]): Record<string, any> {
  const out: Record<string, any> = {};
  for (const def of defs) {
    if (def.type === 'daterange') {
      out[def.name] = def.default ?? { preset: 'this_month' };
    } else if (def.default !== undefined) {
      out[def.name] = def.default;
    } else if (def.type === 'multiselect' || def.type === 'lookup') {
      out[def.name] = [];
    } else {
      out[def.name] = '';
    }
  }
  return out;
}

/**
 * Parameter form.
 *
 * All controls sit in one row above the results they scope — never inside a
 * chart card — so every block re-renders against the same slice.
 */
export function ParamForm({
  defs,
  values,
  onChange,
  disabled,
}: {
  defs: ParamDef[];
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
  disabled?: boolean;
}) {
  const set = (name: string, value: any) => onChange({ ...values, [name]: value });

  return (
    <>
      {defs.map((def) => {
        const label = def.label ?? def.name;
        const value = values[def.name];

        if (def.type === 'daterange') {
          const current = value ?? { preset: 'this_month' };
          return (
            <div key={def.name} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label={label} help={def.help}>
                <select
                  value={current.preset ?? 'this_month'}
                  disabled={disabled}
                  onChange={(e) =>
                    set(def.name, {
                      preset: e.target.value,
                      from: current.from ?? '',
                      to: current.to ?? '',
                    })
                  }
                >
                  {DATE_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </Field>
              {current.preset === 'custom' && (
                <>
                  <Field label="From">
                    <input
                      type="date"
                      value={current.from ?? ''}
                      disabled={disabled}
                      max={current.to || undefined}
                      onChange={(e) => set(def.name, { ...current, preset: 'custom', from: e.target.value })}
                    />
                  </Field>
                  <Field label="To">
                    <input
                      type="date"
                      value={current.to ?? ''}
                      disabled={disabled}
                      min={current.from || undefined}
                      onChange={(e) => set(def.name, { ...current, preset: 'custom', to: e.target.value })}
                    />
                  </Field>
                </>
              )}
            </div>
          );
        }

        if (def.type === 'select') {
          return (
            <Field key={def.name} label={label} help={def.help}>
              <select value={value ?? ''} disabled={disabled} onChange={(e) => set(def.name, e.target.value)}>
                {(def.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
          );
        }

        if (def.type === 'multiselect' || def.type === 'lookup') {
          return (
            <MultiSelect
              key={def.name}
              def={def}
              value={Array.isArray(value) ? value : []}
              disabled={disabled}
              onChange={(next) => set(def.name, next)}
            />
          );
        }

        if (def.type === 'boolean') {
          return (
            <Field key={def.name} label={label} help={def.help}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 31 }}>
                <input
                  type="checkbox"
                  checked={Boolean(value)}
                  disabled={disabled}
                  onChange={(e) => set(def.name, e.target.checked)}
                />
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{value ? 'Yes' : 'No'}</span>
              </span>
            </Field>
          );
        }

        return (
          <Field key={def.name} label={label + (def.required ? ' *' : '')} help={def.help}>
            <input
              type={def.type === 'number' ? 'number' : def.type === 'date' ? 'date' : 'text'}
              value={value ?? ''}
              disabled={disabled}
              onChange={(e) => set(def.name, def.type === 'number' ? e.target.valueAsNumber : e.target.value)}
            />
          </Field>
        );
      })}
    </>
  );
}

/** Lookup-backed multi-select — reads options from a managed list. */
function MultiSelect({
  def,
  value,
  onChange,
  disabled,
}: {
  def: ParamDef;
  value: any[];
  onChange: (next: any[]) => void;
  disabled?: boolean;
}) {
  const [options, setOptions] = useState<{ value: string; label: string }[]>(def.options ?? []);

  useEffect(() => {
    if (def.type !== 'lookup' || !def.lookupTable) return;
    let cancelled = false;
    fetch(`/api/lookups/${def.lookupTable}/rows`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('lookup unavailable'))))
      .then((payload) => {
        if (cancelled) return;
        const valueCol = def.lookupValueColumn ?? 'email';
        const labelCol = def.lookupLabelColumn ?? 'name';
        const rows = (payload.rows ?? []).filter((row: any) =>
          def.lookupFilterColumn ? row[def.lookupFilterColumn] === true || row[def.lookupFilterColumn] === 'true' : true,
        );
        setOptions(rows.map((row: any) => ({ value: String(row[valueCol] ?? ''), label: String(row[labelCol] ?? row[valueCol]) })));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [def.type, def.lookupTable, def.lookupValueColumn, def.lookupLabelColumn, def.lookupFilterColumn]);

  const summary = useMemo(
    () => (value.length === 0 ? 'All' : value.length === 1 ? options.find((o) => o.value === value[0])?.label ?? value[0] : `${value.length} selected`),
    [value, options],
  );

  return (
    <Field label={`${def.label ?? def.name} (${summary})`} help={def.help ?? 'Leave nothing selected to include everyone.'}>
      <select
        multiple
        size={Math.min(6, Math.max(3, options.length))}
        value={value.map(String)}
        disabled={disabled}
        onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}
